import type { CanvasTunnel } from './canvas3d';

/**
 * Canvas redraw coalescer — one full re-render per section operation.
 *
 * WHY THIS EXISTS
 * ---------------
 * The legacy canvas is DATA-BLIND: `view/canvas/tunnel.js` never re-renders
 * because a Backbone collection changed, only when the controller explicitly
 * says so (`show:on:canvas`, `change:tunnel:dimensions`,
 * `validate:tunnel:after:section:destroy`, `redraw:canvas`, resize). Adding a
 * section is therefore exactly ONE full pass (api.js:246-251 stamps the
 * section then fires `show:on:canvas`), and deleting one is exactly ONE pass
 * (section.toolbar.js:413-425, 500 ms after the request).
 *
 * The React board is data-DRIVEN: `AhuCanvas`'s tunnels-changed effect runs
 * `redrawCanvasDependsOnAHU()` on every new `canvasTunnels` identity, and each
 * step of a section operation writes the RTK Query cache in its own tick —
 * optimistic write, tunnel-length recalc, tunnel refetch, type/name stamp,
 * section-list refetch. Stacked on top of the ported explicit passes that is
 * four to six full geometry rebuilds for one click, and the early ones paint
 * states the user should never see (a new Filter drawn as a default-length
 * "Plenum"; a deleted section still standing while the tunnel around it has
 * already shrunk).
 *
 * So: every non-boot full pass is REQUESTED here instead of being run inline.
 * Requests merge into one pass, and a caller running a multi-step operation
 * holds a batch open across it so nothing paints until the data has settled.
 *
 *  - `requestCanvasRedraw({...})` — ask for a pass; several requests in the
 *    same batch/frame collapse into one.
 *  - `beginCanvasRedrawBatch()` — returns the release fn; while any batch is
 *    open no pass runs, so intermediate states never reach the screen.
 *  - `canvasModelSignature()` — lets the caller skip a pass whose input is
 *    byte-identical to the one already on screen (the refetch-returns-the-
 *    same-rows case, which is most of the trailing redraws).
 *
 * The scheduler is module state, not React state, because the producers are
 * bus handlers, RTK Query lifecycles and plain thunks — none of which sit
 * inside the canvas component.
 */

/** What one coalesced pass has to do. */
export interface CanvasRedrawRequest {
  /**
   * Sections whose canvas registries must be dropped before the pass
   * (`validate:tunnel:after:section:destroy`). Deduped, order preserved.
   */
  destroySectionIds: number[];
  /**
   * `show:on:canvas` payload when the pass came from an insert — carries the
   * section to highlight. `undefined` = not requested (null IS a valid
   * legacy payload, hence the sentinel rather than a nullable field).
   */
  showComponent?: unknown;
  /** `change:tunnel:dimensions` payload, when one was requested. */
  dimensions?: {
    tunnel_id: number;
    model?: unknown;
    render?: boolean;
    check?: boolean;
    only_for?: number;
    accessories_fetch?: boolean;
  };
  /** A plain full pass (`redraw:canvas` / tunnels-data change). */
  plain: boolean;
}

export type CanvasRedrawRunner = (request: CanvasRedrawRequest) => void;

/**
 * A batch that never closes would freeze the canvas, and every caller here
 * releases from a `finally` or a timer that a failed request can skip. The
 * watchdog turns "canvas stopped updating" into "canvas updated a beat late".
 */
const BATCH_WATCHDOG_MS = 5000;
/** rAF can be starved (hidden tab); this bounds the wait. */
const FLUSH_FALLBACK_MS = 60;

let runner: CanvasRedrawRunner | null = null;
let pending: CanvasRedrawRequest | null = null;
/** `afterNextCanvasRedraw` subscribers, drained by the next pass that draws. */
const afterPass = new Set<() => void>();
let depth = 0;
let frameId: number | null = null;
let timerId: number | null = null;
let watchdogId: number | null = null;

const emptyRequest = (): CanvasRedrawRequest => ({ destroySectionIds: [], plain: false });

const cancelScheduled = () => {
  if (frameId != null) cancelAnimationFrame(frameId);
  if (timerId != null) window.clearTimeout(timerId);
  frameId = null;
  timerId = null;
};

const flush = () => {
  cancelScheduled();
  if (depth > 0) return; // re-armed by the release
  const request = pending;
  pending = null;
  if (!request || !runner) return;
  runner(request);
  // Drawn — anything waiting for the new geometry can run now.
  [...afterPass].forEach((fn) => fn());
};

/**
 * Deferred to the next frame rather than run inline: the last cache write of
 * an operation lands in a promise continuation, and React commits the render
 * that rebuilds `canvasTunnels` in a task after it. A pass run inline at that
 * point would draw the PREVIOUS model and then need a second pass anyway —
 * the exact double-draw this module exists to remove. rAF is the first hook
 * that is guaranteed to be after the commit.
 */
const schedule = () => {
  if (depth > 0 || !pending || frameId != null || timerId != null) return;
  frameId = requestAnimationFrame(flush);
  timerId = window.setTimeout(flush, FLUSH_FALLBACK_MS);
};

/** AhuCanvas registers the pass executor for its canvas' lifetime. */
export const setCanvasRedrawRunner = (fn: CanvasRedrawRunner | null): void => {
  runner = fn;
  if (!fn) {
    // Canvas gone (unmount / context-loss rebuild): drop the queue rather
    // than replay it into the successor, which boots with a full draw.
    cancelScheduled();
    pending = null;
    return;
  }
  schedule();
};

/** Ask for a full pass. Merges into whatever is already queued. */
export const requestCanvasRedraw = (
  patch: Partial<Omit<CanvasRedrawRequest, 'destroySectionIds'>> & {
    destroySectionId?: number;
  } = {}
): void => {
  const next = pending ?? emptyRequest();
  if (patch.destroySectionId != null && !next.destroySectionIds.includes(patch.destroySectionId)) {
    next.destroySectionIds.push(patch.destroySectionId);
  }
  if ('showComponent' in patch) next.showComponent = patch.showComponent;
  if (patch.dimensions) {
    // Two tunnels' dimension events in one batch can only be satisfied by a
    // full pass — which `render: true` is (canvas3dGeometry
    // onChangeTunnelDimensions wipes every wireframe and re-renders all
    // tunnels), so widen rather than drop one.
    next.dimensions =
      next.dimensions && next.dimensions.tunnel_id !== patch.dimensions.tunnel_id
        ? { ...next.dimensions, render: true }
        : { ...next.dimensions, ...patch.dimensions };
  }
  if (patch.plain) next.plain = true;
  pending = next;
  schedule();
};

/**
 * Hold every pass until the returned fn is called. Ref-counted, so nested
 * operations (an insert whose section patch queues its own batch) behave.
 */
export const beginCanvasRedrawBatch = (): (() => void) => {
  depth += 1;
  if (watchdogId == null) {
    watchdogId = window.setTimeout(() => {
      watchdogId = null;
      if (depth > 0) {
        // eslint-disable-next-line no-console
        console.warn('[ahu-board] canvas redraw batch held too long — flushing');
        depth = 0;
        flush();
      }
    }, BATCH_WATCHDOG_MS);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    depth = Math.max(0, depth - 1);
    if (depth === 0) {
      if (watchdogId != null) window.clearTimeout(watchdogId);
      watchdogId = null;
      schedule();
    }
  };
};

/**
 * Run `fn` once the next coalesced pass has actually drawn.
 *
 * For work that must not reach the screen BEFORE the geometry it belongs to:
 * the post-delete reselect paints its highlight through the canvas, and a
 * delete holds its pass for ~1.5 s (the 500 ms destroy delay plus the re-GETs
 * `holdCanvasRedrawBatchFor` waits on), so running it inline lit the
 * replacement section up while the deleted one was still standing.
 *
 * `fallbackMs` runs it anyway if no pass follows — a failed request queues
 * none, and a dropped highlight would be worse than an early one.
 */
export const afterNextCanvasRedraw = (fn: () => void, fallbackMs = 4000): void => {
  const once = () => {
    if (!afterPass.delete(once)) return;
    fn();
  };
  afterPass.add(once);
  window.setTimeout(once, fallbackMs);
};

/**
 * Hold the canvas until `promise` settles. For the fire-and-forget cache
 * writes an operation kicks off without awaiting (a re-GET for server truth):
 * the operation stays fast, but its one pass still waits for the data rather
 * than drawing without it and correcting itself a moment later.
 */
export const holdCanvasRedrawBatchFor = (promise: PromiseLike<unknown>): void => {
  const release = beginCanvasRedrawBatch();
  Promise.resolve(promise).then(release, release);
};

/** Run `fn` with the canvas held, flushing one pass once it settles. */
export const withCanvasRedrawBatch = async <T>(fn: () => Promise<T>): Promise<T> => {
  const release = beginCanvasRedrawBatch();
  try {
    return await fn();
  } finally {
    release();
  }
};

/**
 * Board unmount — drop scheduler state so the next board boots clean (same
 * contract as resetSectionPatchQueue).
 */
export const resetCanvasRedrawScheduler = (): void => {
  cancelScheduled();
  if (watchdogId != null) window.clearTimeout(watchdogId);
  watchdogId = null;
  pending = null;
  depth = 0;
  // Their canvas is gone; their timers will find the set empty and do nothing.
  afterPass.clear();
};

/**
 * Signature of the canvas' whole input model.
 *
 * `canvasTunnels` (AhuBoardPage) is built for the renderer and holds nothing
 * but geometry-relevant primitives, so a stringify is a complete and stable
 * fingerprint — key order is fixed by the object literal that builds it.
 * Comparing it costs a fraction of a millisecond against the hundreds of
 * meshes a pass rebuilds, and it is what lets a refetch that returns the rows
 * already on screen (the tunnel-list and section-list re-GETs every operation
 * ends with) redraw nothing at all.
 */
export const canvasModelSignature = (tunnels: CanvasTunnel[]): string => JSON.stringify(tunnels);
